/**
 * Utilitário para normalização de nomes de empresas no frontend
 * 
 * Padrão único: usar company_id/slug e derivar a chave de cache disso.
 * Onde for necessário aceitar nome, normalizar (lowercase/trim) antes de compor cache keys.
 */

/**
 * Normalizar nome da empresa para chaves de cache
 * @param {string} empresa Nome da empresa
 * @returns {string} Nome normalizado
 */
export function normalizeCompanyName(empresa) {
  if (!empresa || typeof empresa !== 'string') {
    return 'mov fibra'; // Fallback padrão
  }

  // Normalizar: trim, lowercase, remover espaços extras
  let normalized = empresa.trim().toLowerCase();
  normalized = normalized.replace(/\s+/g, ' '); // Remover espaços múltiplos
  
  console.debug('Company normalized:', {
    original: empresa,
    normalized: normalized
  });
  
  return normalized;
}

/**
 * Gerar chave de cache normalizada para empresa
 * @param {string} empresa Nome da empresa
 * @param {string} action Ação/endpoint
 * @param {object} params Parâmetros adicionais
 * @returns {string} Chave de cache normalizada
 */
export function generateCacheKey(empresa, action, params = {}) {
  const normalizedEmpresa = normalizeCompanyName(empresa);
  const paramsHash = btoa(JSON.stringify(params)).replace(/[^a-zA-Z0-9]/g, '');
  
  return `${action}_${normalizedEmpresa}_${paramsHash}`;
}

/**
 * Gerar chave de cache com prefixo específico
 * @param {string} prefix Prefixo da chave (ex: financeiro, comercial)
 * @param {string} empresa Nome da empresa
 * @param {string} action Ação/endpoint
 * @param {object} params Parâmetros adicionais
 * @returns {string} Chave de cache normalizada
 */
export function generatePrefixedCacheKey(prefix, empresa, action, params = {}) {
  const normalizedEmpresa = normalizeCompanyName(empresa);
  const paramsHash = btoa(JSON.stringify(params)).replace(/[^a-zA-Z0-9]/g, '');
  
  return `${prefix}_${normalizedEmpresa}_${action}_${paramsHash}`;
}

/**
 * Verificar se duas empresas são equivalentes (normalizadas)
 * @param {string} empresa1
 * @param {string} empresa2
 * @returns {boolean}
 */
export function areEquivalent(empresa1, empresa2) {
  return normalizeCompanyName(empresa1) === normalizeCompanyName(empresa2);
}

/**
 * Obter nome padrão normalizado para fallback
 * @returns {string}
 */
export function getDefaultCompany() {
  return 'mov fibra';
}

/**
 * Mapear nome normalizado para slug da empresa
 * @param {string} empresa Nome da empresa
 * @returns {string} Slug da empresa
 */
export function getCompanySlug(empresa) {
  const normalized = normalizeCompanyName(empresa);
  
  const slugMap = {
    'mov fibra': 'mov-fibra',
    'xt fibra': 'xt-fibra',
    'mov fit': 'mov-fit'
  };
  
  return slugMap[normalized] || normalized.replace(/\s+/g, '-');
}

/**
 * Mapear nome normalizado para ID da empresa
 * @param {string} empresa Nome da empresa
 * @returns {number} ID da empresa
 */
export function getCompanyId(empresa) {
  const normalized = normalizeCompanyName(empresa);
  
  const idMap = {
    'mov fibra': 1,
    'xt fibra': 2,
    'mov fit': 3
  };
  
  return idMap[normalized] || 1; // Fallback para MOV Fibra
}

/**
 * Normalizar empresa para uso em APIs
 * @param {string} empresa Nome da empresa
 * @returns {string} Nome normalizado para API
 */
export function normalizeForAPI(empresa) {
  const normalized = normalizeCompanyName(empresa);
  
  // Para APIs, usar formato padrão "MOV Fibra"
  const apiMap = {
    'mov fibra': 'MOV Fibra',
    'xt fibra': 'XT Fibra',
    'mov fit': 'MOV Fit'
  };
  
  return apiMap[normalized] || 'MOV Fibra';
}

const companyNormalizer = {
  normalizeCompanyName,
  generateCacheKey,
  generatePrefixedCacheKey,
  areEquivalent,
  getDefaultCompany,
  getCompanySlug,
  getCompanyId,
  normalizeForAPI
};

export default companyNormalizer;
